// src/features/dashboard/components/ManagerDashboard/IdleTimeReport/AssociateIdleDetailTable.tsx

import { useMemo } from 'react';

import type {
  AssociateIdleDetail,
} from '../../../types/Manager/idleTime.types';

// ============================================================
// TYPES
// ============================================================

type Props = {
  data: AssociateIdleDetail[];
};

type AssociateRow = AssociateIdleDetail;

// ============================================================
// NUMBER HELPER
// ============================================================

const safeNumber = (
  value: unknown,
): number => {
  const numberValue = Number(value);

  if (
    Number.isNaN(numberValue) ||
    !Number.isFinite(numberValue)
  ) {
    return 0;
  }

  return numberValue;
};

// ============================================================
// TIME STRING -> DECIMAL HOURS
//
// API values:
//
// 09:39:36
// 07:06:00
// 02:33:36
// 00:07:48
//
// Result:
//
// 9.66
// 7.10
// 2.56
// 0.13
// ============================================================

const timeToHours = (
  value: unknown,
): number => {
  if (
    value === null ||
    value === undefined
  ) {
    return 0;
  }

  const raw = String(value).trim();

  if (!raw) {
    return 0;
  }

  // ----------------------------------------------------------
  // HH:mm:ss
  // ----------------------------------------------------------

  const timeMatch = raw.match(
    /^(\d+):(\d{2}):(\d{2})$/,
  );

  if (timeMatch) {
    const hours = Number(timeMatch[1]);
    const minutes = Number(timeMatch[2]);
    const seconds = Number(timeMatch[3]);

    if (
      Number.isFinite(hours) &&
      Number.isFinite(minutes) &&
      Number.isFinite(seconds)
    ) {
      return (
        hours +
        minutes / 60 +
        seconds / 3600
      );
    }
  }

  // ----------------------------------------------------------
  // HH:mm
  // ----------------------------------------------------------

  const shortTimeMatch = raw.match(
    /^(\d+):(\d{2})$/,
  );

  if (shortTimeMatch) {
    const hours = Number(
      shortTimeMatch[1],
    );

    const minutes = Number(
      shortTimeMatch[2],
    );

    if (
      Number.isFinite(hours) &&
      Number.isFinite(minutes)
    ) {
      return (
        hours +
        minutes / 60
      );
    }
  }

  // ----------------------------------------------------------
  // Numeric value
  // ----------------------------------------------------------

  const numberValue = Number(raw);

  if (Number.isFinite(numberValue)) {
    return numberValue;
  }

  return 0;
};

// ============================================================
// HOURS FORMAT
//
// Example:
//
// 09:39:36 -> 9.7
// 07:06:00 -> 7.1
// 02:33:36 -> 2.6
// 00:07:48 -> 0.1
// ============================================================

const formatHours = (
  value: unknown,
): string => {
  const hours = timeToHours(value);

  return hours.toFixed(1);
};

// ============================================================
// DATE
// ============================================================

const formatDate = (
  value: unknown,
): string => {
  if (!value) {
    return '-';
  }

  const raw = String(value).trim();

  if (!raw) {
    return '-';
  }

  // ----------------------------------------------------------
  // API:
  // 2026-09-25T00:00:00
  //
  // Display:
  // 25 Sep 2026
  // ----------------------------------------------------------

  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})/,
  );

  if (!match) {
    return raw;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (
    !year ||
    !month ||
    !day
  ) {
    return raw;
  }

  const date = new Date(
    year,
    month - 1,
    day,
  );

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return raw;
  }

  return date.toLocaleDateString(
    'en-GB',
    {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    },
  );
};

// ============================================================
// ROW COLOR
// ============================================================

const getRowClass = (
  status: string,
): string => {
  switch (
    String(status ?? '')
      .trim()
      .toLowerCase()
  ) {
    case 'critical':
      return 'bg-red-50/70';

    case 'warning':
      return 'bg-amber-50/70';

    default:
      return '';
  }
};

// ============================================================
// IDLE BADGE
// ============================================================

const getIdleBadge = (
  value: unknown,
) => {
  const percentage = safeNumber(value);

  if (percentage >= 20) {
    return {
      className:
        'bg-red-100 text-red-700 border border-red-200',
      label: 'Critical',
    };
  }

  if (percentage >= 14) {
    return {
      className:
        'bg-orange-100 text-orange-700 border border-orange-200',
      label: 'Warning',
    };
  }

  return {
    className:
      'bg-green-100 text-green-700 border border-green-200',
    label: 'Low',
  };
};

// ============================================================
// STATUS BADGE
// ============================================================

const getStatusBadge = (
  status: string,
): string => {
  switch (
    String(status ?? '')
      .trim()
      .toLowerCase()
  ) {
    case 'critical':
      return 'bg-red-500 text-white';

    case 'warning':
      return 'bg-yellow-400 text-black';

    case 'good':
      return 'bg-emerald-600 text-white';

    default:
      return 'bg-slate-400 text-white';
  }
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const AssociateIdleDetailTable = ({
  data,
}: Props) => {
  // ==========================================================
  // NORMALIZE + SORT
  // ==========================================================

  const tableData = useMemo<AssociateRow[]>(() => {
    if (!Array.isArray(data)) {
      return [];
    }

    return [...data]
      .filter(Boolean)
      .sort(
        (a, b) =>
          safeNumber(b.idlePercentage) -
          safeNumber(a.idlePercentage),
      );
  }, [data]);

  // ==========================================================
  // DEBUG
  // ==========================================================

  console.log(
    'Associate Idle Detail Table Data:',
    tableData,
  );

  // ==========================================================
  // DATA STATE
  // ==========================================================

  const hasData =
    tableData.length > 0;

  // ==========================================================
  // SUMMARY COUNTS
  // ==========================================================

  const totalAssociates =
    tableData.length;

  const criticalCount =
    tableData.filter(
      (row) =>
        String(row.status ?? '')
          .trim()
          .toLowerCase() ===
        'critical',
    ).length;

  const warningCount =
    tableData.filter(
      (row) =>
        String(row.status ?? '')
          .trim()
          .toLowerCase() ===
        'warning',
    ).length;

  const goodCount =
    tableData.filter(
      (row) =>
        String(row.status ?? '')
          .trim()
          .toLowerCase() ===
        'good',
    ).length;

  // ==========================================================
  // SPAN AVERAGE
  //
  // IMPORTANT:
  // Use timeToHours() instead of Number()
  // because API returns HH:mm:ss.
  // ==========================================================

  const spanAverage = useMemo(() => {
    if (tableData.length === 0) {
      return {
        loggedHours: 0,
        activeHours: 0,
        idleHours: 0,
        idlePercentage: 0,
      };
    }

    const totals = tableData.reduce(
      (acc, row) => ({
        loggedHours:
          acc.loggedHours +
          timeToHours(row.loggedHours),

        activeHours:
          acc.activeHours +
          timeToHours(row.activeHours),

        idleHours:
          acc.idleHours +
          timeToHours(row.idleHours),

        idlePercentage:
          acc.idlePercentage +
          safeNumber(row.idlePercentage),
      }),
      {
        loggedHours: 0,
        activeHours: 0,
        idleHours: 0,
        idlePercentage: 0,
      },
    );

    const count =
      tableData.length;

    return {
      loggedHours:
        totals.loggedHours / count,

      activeHours:
        totals.activeHours / count,

      idleHours:
        totals.idleHours / count,

      idlePercentage:
        totals.idlePercentage / count,
    };
  }, [tableData]);

  // ==========================================================
  // SPAN IDLE BADGE
  // ==========================================================

  const spanIdleBadge =
    getIdleBadge(
      spanAverage.idlePercentage,
    );

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      className="
        w-full
        min-w-0
        overflow-hidden
        rounded-xl
        border
        border-slate-200
        bg-white
        p-3
      "
    >
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div
        className="
          mb-4
          flex
          min-w-0
          flex-wrap
          items-center
          justify-between
          gap-3
        "
      >
        {/* TITLE */}

        <div
          className="
            flex
            min-w-0
            flex-wrap
            items-center
            gap-3
          "
        >
          <h3
            className="
              shrink-0
              text-[15px]
              font-semibold
              text-[#003E7E]
            "
          >
            Associate Idle Time Detail
          </h3>

          <span
            className="
              shrink-0
              rounded-full
              bg-blue-50
              px-2
              py-0.5
              text-[10px]
              font-medium
              text-blue-600
            "
          >
            Sorted by Idle %
          </span>
        </div>

        {/* SUMMARY */}

        {hasData && (
          <div
            className="
              flex
              flex-wrap
              items-center
              gap-2
            "
          >
            {/* TOTAL */}

            <span
              className="
                rounded-full
                bg-slate-100
                px-2
                py-1
                text-[10px]
                font-medium
                text-slate-600
              "
            >
              Total: {totalAssociates}
            </span>

            {/* GOOD */}

            {goodCount > 0 && (
              <span
                className="
                  rounded-full
                  bg-emerald-50
                  px-2
                  py-1
                  text-[10px]
                  font-medium
                  text-emerald-700
                "
              >
                Good: {goodCount}
              </span>
            )}

            {/* WARNING */}

            {warningCount > 0 && (
              <span
                className="
                  rounded-full
                  bg-amber-50
                  px-2
                  py-1
                  text-[10px]
                  font-medium
                  text-amber-700
                "
              >
                Warning: {warningCount}
              </span>
            )}

            {/* CRITICAL */}

            {criticalCount > 0 && (
              <span
                className="
                  rounded-full
                  bg-red-50
                  px-2
                  py-1
                  text-[10px]
                  font-medium
                  text-red-700
                "
              >
                Critical: {criticalCount}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ======================================================
          EMPTY STATE
      ====================================================== */}

      {!hasData ? (
        <div
          className="
            flex
            h-[300px]
            items-center
            justify-center
            rounded-lg
            border
            border-dashed
            border-slate-300
          "
        >
          <div className="text-center">
            <p
              className="
                text-sm
                font-medium
                text-slate-600
              "
            >
              No Associate Data Available
            </p>

            <p
              className="
                mt-1
                text-xs
                text-slate-400
              "
            >
              No associate idle details were
              returned for the selected period.
            </p>
          </div>
        </div>
      ) : (
        /* ====================================================
           TABLE
        ==================================================== */

        <div
          className="
            w-full
            min-w-0
            overflow-x-auto
            overflow-y-hidden
            rounded-lg
            border
            border-slate-200
          "
        >
          <table
            className="
              min-w-[950px]
              w-full
              border-collapse
              text-[11px]
            "
          >
            {/* ==================================================
                HEADER
            ================================================== */}

            <thead>
              <tr
                className="
                  border-b
                  border-slate-200
                  bg-[#F8FAFC]
                  text-[10px]
                  uppercase
                  tracking-wide
                  text-slate-600
                "
              >
                <th
                  className="
                    w-[40px]
                    whitespace-nowrap
                    px-2
                    py-2
                    text-center
                    font-semibold
                  "
                >
                  #
                </th>

                <th
                  className="
                    min-w-[190px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-left
                    font-semibold
                  "
                >
                  Associate
                </th>

                <th
                  className="
                    min-w-[150px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-left
                    font-semibold
                  "
                >
                  Team Lead
                </th>

                <th
                  className="
                    min-w-[110px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-left
                    font-semibold
                  "
                >
                  Date
                </th>

                <th
                  className="
                    min-w-[80px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-left
                    font-semibold
                  "
                >
                  Shift
                </th>

                <th
                  className="
                    min-w-[100px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-left
                    font-semibold
                  "
                >
                  Attendance
                </th>

                <th
                  className="
                    min-w-[90px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-right
                    font-semibold
                  "
                >
                  Logged Hrs
                </th>

                <th
                  className="
                    min-w-[90px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-right
                    font-semibold
                  "
                >
                  Active Hrs
                </th>

                <th
                  className="
                    min-w-[90px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-right
                    font-semibold
                  "
                >
                  Idle Hrs
                </th>

                <th
                  className="
                    min-w-[80px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-center
                    font-semibold
                  "
                >
                  Idle %
                </th>

                <th
                  className="
                    min-w-[90px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-center
                    font-semibold
                  "
                >
                  Idle Status
                </th>

                <th
                  className="
                    min-w-[80px]
                    whitespace-nowrap
                    px-3
                    py-2
                    text-center
                    font-semibold
                  "
                >
                  Status
                </th>
              </tr>
            </thead>

            {/* ==================================================
                BODY
            ================================================== */}

            <tbody>
              {tableData.map(
                (row, index) => {
                  const idlePercentage =
                    safeNumber(
                      row.idlePercentage,
                    );

                  const idleBadge =
                    getIdleBadge(
                      idlePercentage,
                    );

                  const rowStatus =
                    String(
                      row.status ?? '',
                    ).trim();

                  const associateName =
                    String(
                      row.fullName ?? '',
                    ).trim();

                  const teamLeadName =
                    String(
                      row.teamLeadName ?? '',
                    ).trim();

                  return (
                    <tr
                      key={`${row.employeeCode}-${row.loggedDate}-${index}`}
                      className={`
                        border-b
                        border-slate-100
                        transition-colors
                        hover:bg-slate-50
                        ${getRowClass(
                          rowStatus,
                        )}
                      `}
                    >
                      {/* # */}

                      <td
                        className="
                          whitespace-nowrap
                          px-2
                          py-2
                          text-center
                          font-medium
                          text-slate-500
                        "
                      >
                        {index + 1}
                      </td>

                      {/* ASSOCIATE */}

                      <td
                        className="
                          max-w-[220px]
                          px-3
                          py-2
                        "
                      >
                        <p
                          className="
                            truncate
                            font-semibold
                            text-slate-800
                          "
                          title={associateName}
                        >
                          {associateName || '-'}
                        </p>
                      </td>

                      {/* TEAM LEAD */}

                      <td
                        className="
                          max-w-[180px]
                          px-3
                          py-2
                        "
                      >
                        <p
                          className="
                            truncate
                            font-medium
                            text-slate-700
                          "
                          title={teamLeadName}
                        >
                          {teamLeadName || '-'}
                        </p>
                      </td>

                      {/* DATE */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                          text-slate-600
                        "
                      >
                        {formatDate(
                          row.loggedDate,
                        )}
                      </td>

                      {/* SHIFT */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                          text-slate-600
                        "
                      >
                        {row.shiftName || '-'}
                      </td>

                      {/* ATTENDANCE */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                        "
                      >
                        <span
                          className="
                            inline-flex
                            rounded-md
                            bg-slate-100
                            px-2
                            py-0.5
                            text-[10px]
                            font-medium
                            text-slate-600
                          "
                        >
                          {row.attendanceStatus ||
                            '-'}
                        </span>
                      </td>

                      {/* LOGGED HOURS */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                          text-right
                          font-medium
                          tabular-nums
                          text-slate-700
                        "
                      >
                        {formatHours(
                          row.loggedHours,
                        )}
                      </td>

                      {/* ACTIVE HOURS */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                          text-right
                          font-medium
                          tabular-nums
                          text-slate-700
                        "
                      >
                        {formatHours(
                          row.activeHours,
                        )}
                      </td>

                      {/* IDLE HOURS */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                          text-right
                          font-medium
                          tabular-nums
                          text-slate-700
                        "
                      >
                        {formatHours(
                          row.idleHours,
                        )}
                      </td>

                      {/* IDLE % */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                          text-center
                        "
                      >
                        <span
                          className={`
                            inline-flex
                            min-w-[58px]
                            items-center
                            justify-center
                            rounded-full
                            px-2
                            py-0.5
                            text-[10px]
                            font-semibold
                            ${idleBadge.className}
                          `}
                        >
                          {idlePercentage.toFixed(
                            2,
                          )}
                          %
                        </span>
                      </td>

                      {/* IDLE STATUS */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                          text-center
                        "
                      >
                        <span
                          className={`
                            inline-flex
                            rounded-full
                            px-2
                            py-0.5
                            text-[10px]
                            font-medium
                            ${idleBadge.className}
                          `}
                        >
                          {row.idleStatus ||
                            idleBadge.label}
                        </span>
                      </td>

                      {/* STATUS */}

                      <td
                        className="
                          whitespace-nowrap
                          px-3
                          py-2
                          text-center
                        "
                      >
                        <span
                          className={`
                            inline-flex
                            min-w-[54px]
                            items-center
                            justify-center
                            rounded-full
                            px-2
                            py-0.5
                            text-[9px]
                            font-semibold
                            ${getStatusBadge(
                              rowStatus,
                            )}
                          `}
                        >
                          {rowStatus ||
                            'Unknown'}
                        </span>
                      </td>
                    </tr>
                  );
                },
              )}

              {/* ==================================================
                  SPAN AVERAGE
              ================================================== */}

              <tr
                className="
                  border-t-2
                  border-slate-200
                  bg-slate-50
                  font-semibold
                "
              >
                <td
                  colSpan={6}
                  className="
                    whitespace-nowrap
                    px-3
                    py-2
                    text-left
                    text-slate-700
                  "
                >
                  Span Average
                </td>

                {/* LOGGED */}

                <td
                  className="
                    whitespace-nowrap
                    px-3
                    py-2
                    text-right
                    tabular-nums
                    text-slate-800
                  "
                >
                  {spanAverage.loggedHours.toFixed(
                    1,
                  )}
                </td>

                {/* ACTIVE */}

                <td
                  className="
                    whitespace-nowrap
                    px-3
                    py-2
                    text-right
                    tabular-nums
                    text-slate-800
                  "
                >
                  {spanAverage.activeHours.toFixed(
                    1,
                  )}
                </td>

                {/* IDLE */}

                <td
                  className="
                    whitespace-nowrap
                    px-3
                    py-2
                    text-right
                    tabular-nums
                    text-slate-800
                  "
                >
                  {spanAverage.idleHours.toFixed(
                    1,
                  )}
                </td>

                {/* IDLE % */}

                <td
                  className="
                    whitespace-nowrap
                    px-3
                    py-2
                    text-center
                  "
                >
                  <span
                    className={`
                      inline-flex
                      min-w-[58px]
                      items-center
                      justify-center
                      rounded-full
                      border
                      px-2
                      py-0.5
                      text-[10px]
                      font-semibold
                      ${spanIdleBadge.className}
                    `}
                  >
                    {spanAverage.idlePercentage.toFixed(
                      0,
                    )}
                    %
                  </span>
                </td>

                {/* EMPTY */}

                <td
                  colSpan={2}
                  className="
                    whitespace-nowrap
                    px-3
                    py-2
                    text-center
                    text-slate-500
                  "
                >
                  —
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* ======================================================
          FOOTER
      ====================================================== */}

      {hasData && (
        <div
          className="
            mt-2
            flex
            items-center
            justify-between
            gap-2
            text-[9px]
            text-slate-400
          "
        >
          <span>
            Showing {tableData.length}{' '}
            associate
            {tableData.length !== 1
              ? 's'
              : ''}
          </span>

          <span>
            Idle % sorted highest to lowest
          </span>
        </div>
      )}
    </div>
  );
};

export default AssociateIdleDetailTable;